import { paragraphsToPlainText } from '@/lib/shapes/shape-text'
import { isShapeLockedForUserEdit } from '@/lib/groups/element-edit-policy'
import { applyEditToParagraphs } from '@/lib/shapes/shape-text-edits'
import type { ShapeData } from '@/lib/shapes/types/shape-data'
import type { ShapeTextRangeEdit } from '@/lib/shapes/types/shape-text-range-edit'
import type { ShapeTextPatch } from '../types/shape-text-patch'

/** A shape's frame text and its text on a path are separate strings, so each is its own target. */
const targetKey = (edit: ShapeTextRangeEdit) =>
  `${edit.shapeId}\u0000${edit.onPath ? 'path' : 'frame'}`

function readText(shape: ShapeData, onPath: boolean | undefined): string | undefined {
  if (onPath) return shape.textOnPath?.text

  return shape.paragraphs ? paragraphsToPlainText(shape.paragraphs) : undefined
}

/**
 * Works out what a set of replacements writes into `shapes`. Each replacement is a group of ranges
 * written together or not at all, such as the two halves of a word a linked chain cut. A group is
 * refused when any range no longer reads what it `expected`, is in a frame that is locked or sits in
 * a locked group, or overlaps a
 * range already taken. Ranges in one text are applied from the end backwards, so the offsets of the
 * ones before them stay right.
 */
export function planShapeTextReplacements(
  shapes: ShapeData[],
  replacements: readonly (readonly ShapeTextRangeEdit[])[]
): { written: boolean[]; patches: ShapeTextPatch[] } {
  const accepted = new Map<string, ShapeTextRangeEdit[]>()
  const shapesById = new Map(shapes.map((shape) => [shape.id, shape]))

  const isWritable = (edit: ShapeTextRangeEdit) => {
    const shape = shapesById.get(edit.shapeId)

    // The write bypasses ShapeManager's edit guards (freeze must not block text), so a lock on the
    // frame or on any group it sits in has to be refused here.
    if (!shape || isShapeLockedForUserEdit(shape, shapesById)) return false

    const text = readText(shape, edit.onPath)

    if (text === undefined || edit.start < 0 || edit.end < edit.start) return false
    if (text.slice(edit.start, edit.end) !== edit.expected) return false

    return !(accepted.get(targetKey(edit)) ?? []).some(
      (taken) => edit.start < taken.end && taken.start < edit.end
    )
  }

  const written = replacements.map((ranges) => {
    if (ranges.length === 0 || !ranges.every(isWritable)) return false

    for (const edit of ranges) {
      accepted.set(targetKey(edit), [...(accepted.get(targetKey(edit)) ?? []), edit])
    }

    return true
  })

  const patchByShapeId = new Map<string, ShapeTextPatch>()

  for (const edits of accepted.values()) {
    const { shapeId, onPath } = edits[0]!
    const shape = shapesById.get(shapeId)!
    const backwards = [...edits].sort((a, b) => b.start - a.start)
    const patch = patchByShapeId.get(shapeId) ?? { shapeId, before: {}, after: {} }

    if (onPath && shape.textOnPath) {
      let { text } = shape.textOnPath

      for (const edit of backwards) {
        text = text.slice(0, edit.start) + edit.text + text.slice(edit.end)
      }

      patch.before.textOnPath = shape.textOnPath
      patch.after.textOnPath = { ...shape.textOnPath, text }
    } else if (shape.paragraphs) {
      patch.before.paragraphs = shape.paragraphs
      patch.after.paragraphs = backwards.reduce(
        (paragraphs, edit) =>
          applyEditToParagraphs(paragraphs, edit.start, edit.end, edit.text, null),
        shape.paragraphs
      )
    }

    patchByShapeId.set(shapeId, patch)
  }

  return { written, patches: [...patchByShapeId.values()] }
}
